import {Component, Inject, OnInit} from '@angular/core';
import {UntypedFormControl, Validators} from '@angular/forms';
import {MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {WorkflowTransition} from '@nh/angular-tses';
import {DepartureCommentType} from 'app/comment/departure-comment-type';
import {EnvironmentVariableService} from 'app/core/services/environment-variable.service';
import {ReasonCodeService} from 'app/diary/reason-codes/reason-codes.service';
import {RoleService} from '../../../core/services/role.service';
import {DiaryService} from '../../../diary/diary.service';
import {DepartureWorkflowState} from '../../../workflow/departure-workflow-state';
import {Graph} from '../../../workflow/graph';
import {STATE_METADATA} from '../../../workflow/state-metadata';
import {WorkflowStates} from '../../../workflow/workflow-states';
import {WorkflowService} from '../../../workflow/workflow.service';
import {BaseTransitionModalComponent} from '../base-transition-modal/base-transition-modal.component';
import {TransitionInputData} from '../transition-input-data';
import {TransitionOutputData} from '../transition-output-data';
import {UserAssignModalComponent} from '../user-assign-modal/user-assign-modal.component';

export interface RevertTransistionOutputData extends TransitionOutputData {
  to: WorkflowStates;
}

export interface PreviousStateOption {
  workflowState: WorkflowStates;
  displayName: string;
}

@Component({
  selector: 'das-revert-modal',
  templateUrl: './revert-modal.component.html',
  styleUrls: ['./revert-modal.component.scss', '../base-transition-modal/base-transition-modal.component.scss'],
})
export class RevertModalComponent extends BaseTransitionModalComponent implements OnInit {
  public previousStateOptions: PreviousStateOption[] = [];

  public revertStateControl: UntypedFormControl = new UntypedFormControl('', Validators.required);

  private graph: Graph;

  constructor(
    public dialogRef: MatDialogRef<UserAssignModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TransitionInputData,
    roleService: RoleService,
    diaryService: DiaryService,
    reasonCodeService: ReasonCodeService,
    environmentVariableService: EnvironmentVariableService,
    private workflowService: WorkflowService
  ) {
    super(dialogRef, data, roleService, diaryService, reasonCodeService, environmentVariableService);
  }

  public ngOnInit(): void {
    super.ngOnInit();
    Promise.all([
      this.workflowService.getStateGraph(),
      this.diaryService.getWorkflowStateEvents(this.data.departureId),
    ]).then(([transitions, states]: [WorkflowTransition[], DepartureWorkflowState[]]) => {
      this.graph = new Graph(transitions);

      if (states && states.length) {
        const currentState: WorkflowStates = states[states.length - 1].name as WorkflowStates;
        this.previousStateOptions = this.createOptions(states, currentState);
      }
    });
  }

  private createOptions(states: DepartureWorkflowState[], currentState: WorkflowStates): PreviousStateOption[] {
    const seenStates: Record<string, boolean> = {};
    seenStates[currentState] = true;
    return states
      .map((state: DepartureWorkflowState) => state.name as WorkflowStates)
      .filter((state: WorkflowStates) => {
        if (seenStates[state]) {
          return false;
        } else {
          seenStates[state] = true;
          return this.canWalk(state, currentState);
        }
      })
      .map((state: WorkflowStates) => {
        return {
          workflowState: state,
          displayName: STATE_METADATA[state].displayName,
        };
      });
  }

  private canWalk(start: WorkflowStates, end: WorkflowStates): boolean {
    return !!this.graph.getPath(start, end);
  }

  public formIsValid(): boolean {
    return this.inputComment.valid && this.revertStateControl.valid;
  }

  public confirmAndClose(): void {
    const returnData: RevertTransistionOutputData = {
      snapShot: this.generateDiarySnapshot(this.inputComment.value, DepartureCommentType.COMMENT, [], {}),
      cancelled: false,
      to: this.revertStateControl.value,
    };
    this.dialogRef.close(returnData);
  }
}
